import { ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { ImportExportService } from '../../services/import-export.service';
import { DialogService } from '../../services/dialog.service';
import { FileTypeEnum, ImportExportDTO } from '../../models/importExport.model';
import { LinqPredicateDTO } from '@eqproject/eqp-filters';

@Component({
  selector: 'import-export',
  templateUrl: './import-export.component.html',
  styleUrls: ['./import-export.component.scss']
})
export class ImportExportComponent implements OnInit {

  // Commons
  @Input("showImport") showImport: boolean = true;
  @Input("showExport") showExport: boolean = true;
  @Input("showExportTemplate") showExportTemplate: boolean = true;
  @Input("additionalParams") additionalParams: any = null;
  @Input("isDisabled") isDisabled: boolean = false;
  @Input("showRaisedButton") showRaisedButton: boolean = false;
  @Input("fileExtension") fileExtension: string;
  @Input("entityTypeName") entityTypeName: string;
  @Input("exportTemplateLabel") exportTemplateLabel: string = "Esporta template";
  @Input("exportLabel") exportLabel: string = "Esporta";
  @Input("importLabel") importLabel: string = "Importa";
  @Input("mainLabel") mainLabel: string = "Importa - Esporta";
  @Input("mainTooltip") mainTooltip: string = "Importa - Esporta";
  @Input("applyFormatToLabels") applyFormatToLabels: boolean = true;
  @Input("removeHeader") removeHeader: boolean = false;
  @Input("fileName") fileName: string;
  @Input("filters") filters: Array<LinqPredicateDTO>;

  // Csv
  @Input("delimiter") delimiter: string;
  @Input("eol") eol: string;
  @Input("textQualifier") textQualifier: string;
  @Input("numberOfColumns") numberOfColumns: number;
  @Input("areLabelsWithoutFormat") areLabelsWithoutFormat: boolean = true;
  @Input("isHeaderMissing") isHeaderMissing: boolean;

  // Xlsx
  @Input("autofitColumns") autofitColumns: boolean = true;

  @Output("importCompleted") importCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

  fileToImport: Blob;
  url: any;
  importExportDTO: ImportExportDTO = new ImportExportDTO();
  fileToUpload: File = null;

  constructor(private importExportService: ImportExportService) { }

  ngOnInit(): void {
    this.validateParameters();
    this.initImportExportDTO();
  }

  private validateParameters() {
    if (!this.entityTypeName || this.entityTypeName === "") {
      DialogService.Error("Il parametro 'entityTypeName' nel componente 'import-export' è obbligatorio");
      throw new Error("Il parametro 'entityTypeName' nel componente 'import-export' è obbligatorio");
    }
    if (!this.fileExtension || this.fileExtension === "") {
      DialogService.Error("Il parametro 'fileExtension' nel componente 'import-export' è obbligatorio");
      throw new Error("Il parametro 'fileExtension' nel componente 'import-export' è obbligatorio");
    }
    if (this.fileExtension.toLowerCase() !== "csv" && this.fileExtension.toLowerCase() !== "xlsx") {
      DialogService.Error("Il parametro 'fileExtension' può assumere valore 'csv' oppure 'xlsx'");
      throw new Error("Il parametro 'fileExtension' può assumere valore 'csv' oppure 'xlsx'");
    }
    if (this.fileExtension.toLocaleLowerCase() === "csv") {
      if (!this.numberOfColumns) {
        DialogService.Error("Valorizzare il parametro 'numberOfColumns'");
        throw new Error("Valorizzare il parametro 'numberOfColumns'");
      }
      if (this.areLabelsWithoutFormat === null || this.areLabelsWithoutFormat === undefined) {
        DialogService.Error("Valorizzare il parametro 'areLabelsWithoutFormat'");
        throw new Error("Valorizzare il parametro 'areLabelsWithoutFormat'");
      }
      if (this.isHeaderMissing === null || this.isHeaderMissing === undefined) {
        DialogService.Error("Valorizzare il parametro 'isHeaderMissing'");
        throw new Error("Valorizzare il parametro 'isHeaderMissing'");
      }
      if (this.applyFormatToLabels === null || this.applyFormatToLabels === undefined) {
        DialogService.Error("Valorizzare il parametro 'applyFormatToLabels'");
        throw new Error("Valorizzare il parametro 'applyFormatToLabels'");
      }
      if (this.removeHeader === null || this.removeHeader === undefined) {
        DialogService.Error("Valorizzare il parametro 'removeHeader'");
        throw new Error("Valorizzare il parametro 'removeHeader'");
      }
    }
  }

  private initImportExportDTO() {
    this.importExportDTO.FileType = this.fileExtension === "csv" ? FileTypeEnum.csv : FileTypeEnum.xlsx;
    this.importExportDTO.MimeType = this.fileExtension === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    this.importExportDTO.EntityTypeName = this.entityTypeName;
    this.importExportDTO.Delimiter = this.delimiter;
    this.importExportDTO.Eol = this.eol;
    this.importExportDTO.TextQualifier = this.textQualifier;
    this.importExportDTO.ApplyFormatToLabels = this.applyFormatToLabels;
    this.importExportDTO.RemoveHeader = this.removeHeader;
    this.importExportDTO.NumberOfColumns = this.numberOfColumns;
    this.importExportDTO.AreLabelsWithoutFormat = this.areLabelsWithoutFormat;
    this.importExportDTO.IsHeaderMissing = this.isHeaderMissing;
    this.importExportDTO.AutofitColumns = this.autofitColumns;
    this.importExportDTO.Filters = this.filters;
  }

  exportTemplate(): void {
    var self = this;
    this.importExportService.exportTemplate(this.importExportDTO)
      .then((res) => {
        this.downLoadFile(res, this.importExportDTO.MimeType, self.fileName ? self.fileName : `${self.entityTypeName}_template.${self.fileExtension}`);
      }).catch((err) => {
        DialogService.Error(err.message);
      });
  }

  export(): void {
    var self = this;
    this.initImportExportDTO();
    this.importExportService.exportFromIBaseEntity(this.importExportDTO, this.additionalParams)
      .then((res) => {
        this.downLoadFile(res, this.importExportDTO.MimeType, self.fileName ? self.fileName : `${self.entityTypeName}.${self.fileExtension}`);
      }).catch((err) => {
        DialogService.Error(err.message);
      });
  }

  import(event: any): void {
    if (!event.target.files || !event.target.files[0])
      return;

    this.fileToUpload = event.target.files[0];
    var reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);

    reader.onload = (event) => {
      this.url = event.target.result;
      this.fileToImport = this.convertBase64ToBlob(this.url);
      this.importExportService.import(this.fileToImport, this.importExportDTO, this.additionalParams).then((res) => {
        DialogService.Success("Operazione completata");
        if (this.importCompleted)
          this.importCompleted.emit(true);
        if (res.HasErrors == true && res.FileBase64) {
          DialogService.Warning("L'operazione è stata completata ma ci sono alcuni record che non è stato possibile importare");
          let a = document.createElement("a");
          a.href = "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," + res.FileBase64;
          a.download = `${this.entityTypeName}_import_errors.xlsx`;
          a.click();
        }
      }).catch((err) => {
        DialogService.Error(err.message);
        if (this.importCompleted)
          this.importCompleted.emit(false);
      })
        .finally(() => {
          this.resetFileInput();
        });
    }
  }

  resetFileInput() {
    if (this.fileInput)
      this.fileInput.nativeElement.value = "";

    this.fileToImport = null;
  }

  convertBase64ToBlob(base64Image: string) {
    // Split into two parts
    const parts = base64Image.split(';base64,');

    // Hold the content type
    const imageType = parts[0].split(':')[1];

    // Decode Base64 string
    const decodedData = window.atob(parts[1]);

    // Create UNIT8ARRAY of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length);

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }

    // Return BLOB image after conversion
    return new Blob([uInt8Array], { type: imageType });
  }

  downLoadFile(data: any, type: string, fileName: string) {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';

    let blob = new Blob([data.body], { type: type });
    let url = window.URL.createObjectURL(blob);

    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
